import * as z from "zod/v4";

const timePeriodSchema = z.enum([
  "day",
  "week",
  "month",
  "last-month",
  "quarter",
  "all",
  "custom",
]);

const customRangeSchema = z.object({
  start: z.string().min(1),
  end: z.string().min(1),
});

const funnelModeSchema = z.enum(["weighted", "fully-onboarded"]);

const statusFilterSchema = z.enum([
  "Completed",
  "Reopened",
  "Awaiting approval",
  "In progress",
  "Partial Completion",
  "Help Requested",
]);

const numericFilterSchema = z.object({
  operator: z.enum(["lt", "gt"]),
  value: z.number().min(0),
});

const dateRangeFilterSchema = z.object({
  start: z.string().min(1),
  end: z.string().min(1),
});

export const funnelStepSchema: z.ZodType<any> = z.lazy(() =>
  z.object({
    stepName: z.string(),
    stepOrder: z.number().int(),
    completed: z.number().int().min(0),
    percentage: z.number().min(0).max(100),
    dropoffFromPrevious: z.number().min(0).max(100).optional(),
    dropoffCount: z.number().int().min(0).optional(),
    averageTimeMinutes: z.number().min(0).optional(),
    substeps: z.array(funnelStepSchema).optional(),
  }),
);

export const selfServeOnboardingFunnelStepsSchema = z.object({
  totalStarted: z.number().int().min(0),
  steps: z.array(funnelStepSchema),
});

export const getSelfServeOnboardingFunnelSteps = {
  name: "getSelfServeOnboardingFunnelSteps" as const,
  input: z
    .object({
      timePeriod: timePeriodSchema,
      customRange: customRangeSchema.optional(),
      funnelMode: funnelModeSchema.optional(),
      // Advanced filters (matching stats/time-series endpoints)
      filterLowIntent: z.boolean().optional(),
      typeFilter: z.enum(["all", "Self-Serve", "Custom"]).optional(),
      statuses: z.array(statusFilterSchema).optional(),
      configurationPushedFilter: z.enum(["yes", "no"]).optional(),
      startDateRange: dateRangeFilterSchema.optional(),
      durationFilter: numericFilterSchema.optional(),
      dealSizeFilter: numericFilterSchema.optional(),
    })
    .superRefine((value, ctx) => {
      if (value.timePeriod === "custom" && !value.customRange) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "customRange is required when timePeriod is custom",
          path: ["customRange"],
        });
      }
    }),
  output: selfServeOnboardingFunnelStepsSchema,
};

export type FunnelStep = z.infer<typeof funnelStepSchema>;
export type SelfServeOnboardingFunnelSteps = z.infer<
  typeof selfServeOnboardingFunnelStepsSchema
>;
export type GetSelfServeOnboardingFunnelStepsInput = z.infer<
  typeof getSelfServeOnboardingFunnelSteps.input
>;
export type GetSelfServeOnboardingFunnelStepsOutput = z.infer<
  typeof getSelfServeOnboardingFunnelSteps.output
>;
